import { queryOptions } from "@tanstack/react-query";

import { api, mBrandResearchBaseURL } from "@/config/api";
import { createQueryString, createURLPath } from "@/lib/utils";
import {
  SavedPromptListSchema,
  SavedPromptSchema,
  type SavedPrompt,
  type SavedPromptInput,
} from "@/schema/brand-os/paid-media-prompts";

/**
 * Saved insights prompts — CRUD against
 * `/api/v1/ads-intelligence/insights/prompts`. All endpoints are scoped by
 * `user_id` (the auth wrapper isn't in place yet — pass it explicitly on
 * every call).
 *
 * Most prompts are now created *implicitly* by `POST /insights/run` — the
 * backend dedupes on exact text and persists. The `Create` API here is
 * therefore rarely needed; the more common flows are `List` (drawer
 * render), `Update` (rename — bumps `version` for client-side cache
 * busting), and `Delete` (housekeeping).
 */

export class PaidMediaPromptApiError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = "PaidMediaPromptApiError";
    this.status = status;
  }
}

async function parseError(response: Response): Promise<PaidMediaPromptApiError> {
  const raw = (await response.json().catch(() => ({}))) as {
    error?: string;
    detail?: string;
    message?: string;
  };
  const message =
    raw.error || raw.detail || raw.message || `Request failed (${response.status})`;
  return new PaidMediaPromptApiError(response.status, message);
}

const ENDPOINT = mBrandResearchBaseURL + "/api/v1/ads-intelligence/insights/prompts";

function buildListUrl(userId: string) {
  const qs = createQueryString({ user_id: userId });
  return qs ? `${ENDPOINT}?${qs}` : ENDPOINT;
}

function buildItemUrl(promptId: string, userId: string) {
  const base = createURLPath(ENDPOINT, promptId);
  const qs = createQueryString({ user_id: userId });
  return qs ? `${base}?${qs}` : base;
}

export const PaidMediaPromptApiFactory = {
  Endpoint: ENDPOINT,
  Keys: {
    List: (userId: string) => ["paid-media-prompts", "list", userId] as const,
    Item: (promptId: string, userId: string) =>
      ["paid-media-prompts", "item", userId, promptId] as const,
  },
  Apis: {
    /**
     * List the user's saved prompts. Returns `[]` for a fresh user — the
     * drawer must render an empty state. (The pre-revamp auto-seeding of a
     * default prompt is gone now that prompts are created implicitly by
     * `/insights/run`.)
     */
    List: async (userId: string): Promise<SavedPrompt[]> => {
      const response = await api(buildListUrl(userId));
      if (!response.ok) throw await parseError(response);
      return response.json().then(SavedPromptListSchema.parse);
    },
    Get: async (promptId: string, userId: string): Promise<SavedPrompt> => {
      const response = await api(buildItemUrl(promptId, userId));
      if (!response.ok) throw await parseError(response);
      return response.json().then(SavedPromptSchema.parse);
    },
    Create: async (userId: string, body: SavedPromptInput): Promise<SavedPrompt> => {
      const response = await api(buildListUrl(userId), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!response.ok) throw await parseError(response);
      return response.json().then(SavedPromptSchema.parse);
    },
    Update: async (
      promptId: string,
      userId: string,
      body: SavedPromptInput,
    ): Promise<SavedPrompt> => {
      const response = await api(buildItemUrl(promptId, userId), {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!response.ok) throw await parseError(response);
      return response.json().then(SavedPromptSchema.parse);
    },
    Delete: async (promptId: string, userId: string): Promise<void> => {
      const response = await api(buildItemUrl(promptId, userId), { method: "DELETE" });
      if (!response.ok && response.status !== 204) throw await parseError(response);
    },
  },
  Queries: {
    List: (userId: string) =>
      queryOptions({
        queryKey: PaidMediaPromptApiFactory.Keys.List(userId),
        queryFn: () => PaidMediaPromptApiFactory.Apis.List(userId),
        staleTime: 5 * 60_000,
        gcTime: 30 * 60_000,
        // List endpoint is cheap — single retry on transient failure is fine.
        retry: 1,
      }),
  },
};
